import type {
  NetworkNode,
  ProfileInput,
  ResellerNetwork,
  ResellerPermission,
  ResellerProfile,
  ResellerRoleKey,
  RoleMatrix,
  TeamMember,
  TeamRoster,
} from "@xop-any-time/shared";

import { api } from "../../lib/api.js";

/**
 * The account, team and network surfaces.
 *
 * WHERE EACH CALL GOES, because it is not uniform and the reason matters:
 *
 *   • `/api/profile/*` — the BFF's own typed routes. Used wherever the BFF
 *     actually does work: talking to portal-api, or ordering a write across
 *     BOTH boxes (crm-api first, portal-api second — see the server's
 *     `routes/account.ts`).
 *   • `/api/crm/v1/*` — the existing passthrough. Used for the network READ,
 *     which is a plain crm-api GET with no orchestration to do. Adding a
 *     typed route for it would be a second description of a payload that has
 *     one consumer.
 */

export const accountKeys = {
  profile: ["profile"] as const,
  team: ["team"] as const,
  network: ["network"] as const,
  roleMatrix: ["role-matrix"] as const,
};

/* ── Profile ─────────────────────────────────────────────────────────── */

export const getProfile = (): Promise<{ profile: ResellerProfile }> =>
  api<{ profile: ResellerProfile }>("/api/profile");

export const updateProfile = (
  input: ProfileInput,
): Promise<{ account: { firstName: string | null; lastName: string | null } }> =>
  api("/api/profile", { method: "PATCH", body: input });

export const changePassword = (input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ ok: true }> => api("/api/profile/password", { method: "POST", body: input });

/* ── Team ────────────────────────────────────────────────────────────── */

export const getTeam = (): Promise<TeamRoster> => api<TeamRoster>("/api/profile/team");

/** `invited` is false when the AUTHORIZATION was written but the invitation
 * email could not be sent. Both facts are shown — the person has access and
 * needs a resend, which is a different problem from "nothing happened". */
export const inviteMember = (input: {
  email: string;
  role: ResellerRoleKey;
}): Promise<{ member: TeamMember; invited: boolean; message?: string }> =>
  api("/api/profile/team", { method: "POST", body: input });

export const setMemberRole = (
  id: number,
  role: ResellerRoleKey,
): Promise<{ member: TeamMember }> =>
  api(`/api/profile/team/${id}`, { method: "PATCH", body: { role } });

export const revokeMember = (id: number): Promise<{ member: TeamMember }> =>
  api(`/api/profile/team/${id}`, { method: "DELETE" });

export const setMemberPermissions = (
  id: number,
  input: { permissionGrants: ResellerPermission[]; permissionDenies: ResellerPermission[] },
): Promise<{ member: TeamMember }> =>
  api(`/api/profile/team/${id}`, { method: "PATCH", body: input });

/* ── Roles (the company's matrix, spec F §6.4) ───────────────────────── */

export const getRoleMatrix = (): Promise<RoleMatrix> => api<RoleMatrix>("/api/profile/role-permissions");

/** `role` is a built-in editable role or a `custom_…` key. */
export const setRoleMatrix = (
  role: string,
  permissions: ResellerPermission[],
): Promise<RoleMatrix> =>
  api(`/api/profile/role-permissions/${role}`, { method: "PUT", body: { permissions } });

/** A built-in returns to XOP's shipped bundle. */
export const resetRoleMatrix = (role: string): Promise<RoleMatrix> =>
  api(`/api/profile/role-permissions/${role}`, { method: "DELETE" });

/* ── Custom roles (2026-09-15) ───────────────────────────────────────── */

/** A new role starts with no permissions; the owner ticks them in the matrix. */
export const createRole = (name: string): Promise<RoleMatrix> =>
  api("/api/profile/role-permissions", { method: "POST", body: { name, permissions: [] } });

/** PUT always carries the permissions, so a rename re-sends the current set. */
export const renameRole = (
  role: string,
  name: string,
  permissions: ResellerPermission[],
): Promise<RoleMatrix> =>
  api(`/api/profile/role-permissions/${role}`, { method: "PUT", body: { permissions, name } });

/** The same DELETE as a reset — on a custom key crm-api removes the role, or
 * answers 409 ROLE_IN_USE while someone still holds it. */
export const deleteCustomRole = (role: string): Promise<RoleMatrix> =>
  api(`/api/profile/role-permissions/${role}`, { method: "DELETE" });

/* ── Network ─────────────────────────────────────────────────────────── */

/**
 * crm-api answers snake_case here (it is the passthrough, not a projected
 * route), so this is the one place that translates. Kept as a function rather
 * than sprinkled through the component so the page never sees two spellings
 * of the same field.
 */
interface RawNode {
  id: number;
  name: string;
  code: string | null;
  parent_reseller_id: number | null;
  created_at: string | null;
  customer_count?: number;
  member_count?: number;
}

function toNode(raw: RawNode): NetworkNode {
  return {
    id: raw.id,
    name: raw.name,
    code: raw.code,
    parentResellerId: raw.parent_reseller_id,
    createdAt: raw.created_at,
    ...(typeof raw.customer_count === "number" ? { customerCount: raw.customer_count } : {}),
    ...(typeof raw.member_count === "number" ? { memberCount: raw.member_count } : {}),
  };
}

export async function getNetwork(): Promise<ResellerNetwork> {
  const raw = await api<{
    reseller: RawNode;
    ancestors: RawNode[];
    depth: number;
    descendants: RawNode[];
    viewer: { email: string; role: ResellerRoleKey };
  }>("/api/crm/v1/reseller-network");

  return {
    reseller: toNode(raw.reseller),
    ancestors: (raw.ancestors ?? []).map(toNode),
    depth: raw.depth ?? 0,
    descendants: (raw.descendants ?? []).map(toNode),
    viewer: raw.viewer,
  };
}

export const createChild = (input: {
  name: string;
  ownerEmail: string;
}): Promise<{ reseller: RawNode; invited: boolean; message?: string }> =>
  api("/api/profile/network/children", { method: "POST", body: input });
